import { useCallback } from 'react';
import {
  generateEventID,
  generateSHA1Hash,
  useImpactSessionID,
  useImRef,
} from './utils';

const ACTION_TRACKER_ID_REGISTRATION = 60396;

/**
 * Manages impact affiliate tracking.
 *
 * This is a wrapper around the impact SDK that provides a context for tracking conversions.
 *
 * Intended to replace rewardful (apps/deprecated-web/src/client/App/features/rewardful)
 */
export const useImpact = () => {
  const imRef = useImRef();
  const sessionID = useImpactSessionID();

  /**
   * Check if the user has a currently stored referral code for an impact affiliate.
   */
  const hasReferral = useCallback(() => {
    return imRef.get() !== undefined;
  }, [imRef]);

  const trackConversion = useCallback(
    (userID: string, userEmail: string) => {
      const track = async () => {
        const emailHash = userEmail ? await generateSHA1Hash(userEmail) : '';
        const orderId = generateEventID();

        window?.ire?.(
          'trackConversion',
          ACTION_TRACKER_ID_REGISTRATION,
          {
            orderId,
            clickId: imRef.get() ?? '',
            customerId: userID,
            customerEmail: emailHash,
            customProfileId: sessionID.get(),
          },
          { verifySiteDefinitionMatch: true }
        );
      };

      if (window?.ire !== undefined) {
        track();
      } else {
        console.error('Impact SDK not initialized');
      }
    },
    [imRef, sessionID]
  );

  return {
    hasReferral,
    trackConversion,
  };
};
